import React from 'react';
import ScheduleRow from './ScheduleRow';
import type {
  PublicScheduleItem,
  ScheduleStationPayload,
} from '../../../../../interfaces/schedule';

type Props = {
  title: string;
  items?: PublicScheduleItem[];
  activeStationId: string;
  stations: ScheduleStationPayload[];
  showNowButton?: boolean;
  enableNowPlayingHighlight?: boolean;
  onChooseOtherStations?: () => void;
  onSelectStation: (stationId: string) => void;
  onExpansionChange?: (isExpanded: boolean) => void;
};

export default function ScheduleGrid({
  title,
  items = [],
  activeStationId,
  stations,
  showNowButton = false,
  enableNowPlayingHighlight = false,
  onSelectStation,
  onExpansionChange,
}: Props) {
  const [isStationsOpen, setIsStationsOpen] = React.useState(false);
  const [isFullScheduleOpen, setIsFullScheduleOpen] = React.useState(false);
  const [canScrollUp, setCanScrollUp] = React.useState(false);
  const [canScrollDown, setCanScrollDown] = React.useState(false);

  const listRef = React.useRef<HTMLDivElement | null>(null);
  const activeItemRef = React.useRef<HTMLDivElement | null>(null);
  const hasScrolledToActiveRef = React.useRef(false);
  const expansionDirectionRef = React.useRef<'above' | 'below' | null>(null);
  const shouldFocusActiveAfterCollapseRef = React.useRef(false);
  const scheduleListId = React.useId().replace(/:/g, '');

  const updateScrollHints = React.useCallback(() => {
    const listEl = listRef.current;

    if (!listEl) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } = listEl;
    const threshold = 4;

    setCanScrollUp(scrollTop > threshold);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - threshold);
  }, []);

  const toggleFullSchedule = React.useCallback(
    (direction: 'above' | 'below') => {
      setIsFullScheduleOpen((isOpen) => {
        if (isOpen) {
          hasScrolledToActiveRef.current = true;
          expansionDirectionRef.current = null;
          shouldFocusActiveAfterCollapseRef.current = true;
        } else {
          expansionDirectionRef.current = direction;
        }

        return !isOpen;
      });
    },
    [],
  );

  React.useEffect(() => {
    onExpansionChange?.(isFullScheduleOpen);
  }, [isFullScheduleOpen, onExpansionChange]);

  React.useEffect(() => {
    if (!isFullScheduleOpen || !expansionDirectionRef.current) return;

    const direction = expansionDirectionRef.current;
    let secondFrame = 0;

    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        activeItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: direction === 'below' ? 'start' : 'end',
        });
        expansionDirectionRef.current = null;
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [isFullScheduleOpen]);

  React.useEffect(() => {
    if (isFullScheduleOpen || !shouldFocusActiveAfterCollapseRef.current) {
      return;
    }

    let secondFrame = 0;

    const firstFrame = window.requestAnimationFrame(() => {
      const listEl = listRef.current;
      const activeEl = activeItemRef.current;

      if (!listEl || !activeEl) {
        shouldFocusActiveAfterCollapseRef.current = false;
        return;
      }

      const targetScrollTop =
        activeEl.offsetTop -
        listEl.clientHeight / 2 +
        activeEl.clientHeight / 2;

      listEl.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'auto',
      });

      secondFrame = window.requestAnimationFrame(() => {
        activeEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        shouldFocusActiveAfterCollapseRef.current = false;
        updateScrollHints();
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [isFullScheduleOpen, updateScrollHints]);

  React.useEffect(() => {
    hasScrolledToActiveRef.current = false;
  }, [activeStationId]);

  React.useEffect(() => {
    if (!items.length) {
      updateScrollHints();
      return;
    }

    if (isFullScheduleOpen) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }

    const hasNowPlayingItem = items.some((item) => item.isNowPlaying);

    if (!hasNowPlayingItem) {
      hasScrolledToActiveRef.current = false;

      window.requestAnimationFrame(() => {
        updateScrollHints();
      });

      return;
    }

    if (
      !hasScrolledToActiveRef.current &&
      listRef.current &&
      activeItemRef.current
    ) {
      const listEl = listRef.current;
      const activeEl = activeItemRef.current;

      const targetScrollTop =
        activeEl.offsetTop -
        listEl.clientHeight / 2 +
        activeEl.clientHeight / 2;

      listEl.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth',
      });

      hasScrolledToActiveRef.current = true;

      window.setTimeout(() => {
        updateScrollHints();
      }, 260);

      return;
    }

    window.requestAnimationFrame(() => {
      updateScrollHints();
    });
  }, [items, activeStationId, isFullScheduleOpen, updateScrollHints]);

  React.useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    updateScrollHints();

    const handleScroll = () => updateScrollHints();
    const handleResize = () => updateScrollHints();

    listEl.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      listEl.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [updateScrollHints]);

  return (
    <section className="schedule-grid" aria-labelledby="schedule-grid-title">
      <div
        className={`schedule-grid__list-wrap ${
          !canScrollUp ? 'hide-top' : ''
        } ${!canScrollDown ? 'hide-bottom' : ''} ${
          isFullScheduleOpen ? 'is-expanded' : ''
        }`}
      >
        {canScrollUp || isFullScheduleOpen ? (
          <div className="schedule-grid__scroll-hint schedule-grid__scroll-hint--top">
            <button
              type="button"
              className="schedule-grid__scroll-button"
              onClick={() => toggleFullSchedule('above')}
              aria-expanded={isFullScheduleOpen}
              aria-controls={scheduleListId}
            >
              {isFullScheduleOpen
                ? '↕ Condensar grade'
                : '↑ Mais horários acima'}
            </button>
          </div>
        ) : null}

        <div
          id={scheduleListId}
          ref={listRef}
          className={`schedule-grid__list${
            isFullScheduleOpen ? ' is-expanded' : ''
          }`}
          aria-label="Lista de horários da programação"
        >
          {items.map((item) => {
            const isActive = enableNowPlayingHighlight && item.isNowPlaying;

            return (
              <div
                key={item.id}
                ref={isActive ? activeItemRef : null}
                className={
                  isActive
                    ? 'schedule-grid__row-anchor is-active'
                    : 'schedule-grid__row-anchor'
                }
              >
                <ScheduleRow
                  item={item}
                  isNowPlaying={isActive}
                  isActive={isActive}
                  stationId={activeStationId}
                  showNowButton={showNowButton}
                />
              </div>
            );
          })}
        </div>

        {canScrollDown || isFullScheduleOpen ? (
          <div className="schedule-grid__scroll-hint schedule-grid__scroll-hint--bottom">
            <button
              type="button"
              className="schedule-grid__scroll-button"
              onClick={() => toggleFullSchedule('below')}
              aria-expanded={isFullScheduleOpen}
              aria-controls={scheduleListId}
            >
              {isFullScheduleOpen
                ? '↕ Condensar grade'
                : '↓ Mais horários abaixo'}
            </button>
          </div>
        ) : null}
      </div>

      <div className="schedule-grid__footer">
        <button
          type="button"
          className={`schedule-grid__footer-trigger ${
            isStationsOpen ? 'is-open' : ''
          }`}
          onClick={() => setIsStationsOpen((prev) => !prev)}
          aria-expanded={isStationsOpen}
          aria-controls="schedule-grid-stations-panel"
        >
          <div className="schedule-grid__footer-copy">
            <span>Ver outras rádios</span>
            <small>Selecione aqui</small>
          </div>

          <span className="schedule-grid__footer-icon" aria-hidden="true">
            {isStationsOpen ? '⌃' : '⌄'}
          </span>
        </button>

        {isStationsOpen ? (
          <div
            id="schedule-grid-stations-panel"
            className="schedule-grid__stations-panel"
          >
            {stations.map((station) => (
              <button
                key={station.id}
                type="button"
                className={`schedule-grid__station-item ${
                  station.id === activeStationId ? 'is-active' : ''
                }`}
                onClick={() => {
                  onSelectStation(station.id);
                  setIsStationsOpen(false);
                }}
              >
                <span>{station.title}</span>
                {station.shortTitle ? (
                  <small>{station.shortTitle}</small>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
